// Single source of truth for wizard step validation.
//
// Both FormWizard.js (per-step Next button gating) and ReviewStep.js
// (pre-submit completeness check) import from here. Without this, the
// two paths drift and a Skip-to-Review user can hit Submit with a
// malformed postcode / sub-18 DOB / missing vehicle make that the
// per-step Next button would have caught.
//
// Pure functions: no React, no hooks, no context. Inputs are formData
// (object) and applicationMode (string | null). Outputs are strings
// (error messages) or null.

import { additionalIncomeError } from './additionalIncome';
import { isValidUKPostcode } from './ukPostcode';
import { isValidUKVehicleReg } from './ukVehicleReg';

// Step id list in wizard order. Mirrors STEPS in FormWizard.js — keep in sync.
// Step indexes are derived from this list so the "Click to fix" chips in
// Review can jump to the right step via goToStep(index).
export const STEP_IDS = [
  'dealer',
  'vehicle',
  'finance-type',
  'application-type',
  'licence',
  'marital',
  'passport',
  'nationality',
  'dob',
  'personal-contact',
  'address',
  'employment-status',
  'employment-details',
  'income',
  'bank',
  'notes',
  'review',
];

export const STEP_INDEX = STEP_IDS.reduce((acc, id, i) => {
  acc[id] = i;
  return acc;
}, {});

// Friendly labels for error grouping in Review. Match the navbar pills.
export const STEP_LABELS = {
  dealer: 'Dealer',
  vehicle: 'Vehicle',
  'finance-type': 'Finance Type',
  'application-type': 'Application Type',
  licence: 'Driving Licence',
  marital: 'Marital Status',
  passport: 'Passport',
  nationality: 'Nationality',
  dob: 'Date of Birth',
  'personal-contact': 'Personal & Contact',
  address: 'Address',
  'employment-status': 'Employment Status',
  'employment-details': 'Employment Details',
  income: 'Income',
  bank: 'Bank',
  notes: 'Notes',
  review: 'Review',
};

const EMPLOYED_STATUSES = [
  'Full-Time Employment',
  'Part-Time Employment',
  'Self-Employed',
  'Temporary/Contract',
  'Armed Forces',
];

/**
 * Validate a single step. Returns an error string, or null if the step
 * is currently valid.
 */
export const validateStep = (stepId, formData = {}, applicationMode = null, opts = {}) => {
  switch (stepId) {
    case 'dealer':
      if (!formData.dealer_name?.trim()) return 'Please enter the dealer name';
      return null;

    case 'vehicle':
      if (!applicationMode) return 'Please choose what you\'re applying for';
      // Credit-line mode: applicant hasn't chosen a vehicle yet, broker will
      // assign a dummy car server-side. Only the desired borrow amount is required.
      if (applicationMode === 'credit-line') {
        if (!formData.borrow_amount?.trim() || parseFloat(formData.borrow_amount) <= 0) {
          return 'Please enter the amount you\'d like to borrow';
        }
        return null;
      }
      if (!formData.vehicle_type) return 'Please select a vehicle type';
      // PDF-email sources only: customer may describe the car instead of giving a reg.
      if (opts.allowNoReg && formData.vehicle_reg_unknown) {
        if ((formData.vehicle_description || '').trim().length < 3) {
          return 'Please describe the vehicle (e.g. Blue Ford Fiesta 2018)';
        }
      } else {
      if (!formData.vehicle_registration?.trim()) return 'Please enter vehicle registration';
      if (!isValidUKVehicleReg(formData.vehicle_registration)) {
        return `Vehicle registration "${formData.vehicle_registration}" doesn't look like a UK plate (expected e.g. AB12 CDE). Please check and re-run the DVLA lookup.`;
      }
      // Auto-lookup runs on input, but if it failed (DVLA down, unknown plate, etc.)
      // vehicle_make can still be empty. Catch this here with a clear message
      // pointing the user at the Lookup button — far better UX than letting the
      // backend reject the whole submission later.
      if (!formData.vehicle_make?.trim()) {
        return 'Vehicle Make is missing — click the Lookup button next to the registration, or type the make manually below it.';
      }
      if (!formData.vehicle_mileage?.trim()) return 'Please enter vehicle mileage';
      }
      if (!formData.cash_price?.trim()) return 'Please enter cash price';
      {
        // Sanity bounds against data-entry typos. UK car finance minimums
        // typically start ~£1k (below that = missed a digit, e.g. £17.20
        // meant £17,200). £300k is a comfortable ceiling above super-cars
        // and light commercial — anything higher is almost always an extra
        // zero (e.g. £150,000 → £15,000, or an OCR paste from an unrelated
        // field). Both bounds are HARD blocks and share the ×10/×1000 hint.
        const cp = parseFloat(String(formData.cash_price).replace(/[£,\s]/g, ''));
        if (!Number.isFinite(cp) || cp <= 0) return 'Cash price must be greater than zero';
        if (cp < 1000) return `Cash price "£${cp.toFixed(2)}" looks too low — did you mean £${(cp * 1000).toLocaleString('en-GB')}? Please double-check the value.`;
        if (cp > 300000) {
          const hint = cp / 10;
          const hintStr = hint.toLocaleString('en-GB', {
            minimumFractionDigits: hint % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
          });
          return `Cash price "£${cp.toLocaleString('en-GB')}" looks too high — did you mean £${hintStr}? Please double-check the value.`;
        }
      }
      return null;

    case 'licence':
      if (!formData.driving_licence_type) return 'Please select your licence type';
      return null;

    case 'marital':
      if (!formData.marital_status) return 'Please select your marital status';
      return null;

    case 'passport':
      return null; // optional

    case 'nationality':
      return null; // optional

    case 'dob': {
      if (!formData.date_of_birth || formData.date_of_birth.includes('//')) {
        return 'Please enter your date of birth';
      }
      // Hard age guardrails. The DOB dropdown already restricts the year range
      // but OCR extraction can inject any date — catch that here.
      const parts = formData.date_of_birth.split('/');
      if (parts.length === 3) {
        const [d, m, y] = parts.map((p) => parseInt(p, 10));
        if (d && m && y && y > 1900) {
          const dob = new Date(y, m - 1, d);
          if (!isNaN(dob.getTime())) {
            const today = new Date();
            let age = today.getFullYear() - dob.getFullYear();
            const mDiff = today.getMonth() - dob.getMonth();
            if (mDiff < 0 || (mDiff === 0 && today.getDate() < dob.getDate())) age--;
            if (age < 18) return `Applicant must be 18 or over (currently ${age}). Please check the date of birth.`;
            if (age > 100) return `Date of birth looks wrong (age ${age}). Please double-check.`;
          }
        }
      }
      return null;
    }

    case 'personal-contact':
      if (!formData.title) return 'Please select your title';
      if (!formData.first_name?.trim()) return 'Please enter your first name';
      if (!formData.surname?.trim()) return 'Please enter your surname';
      if (!formData.mobile_number?.trim()) return 'Please enter your mobile number';
      if (!formData.email_address?.trim()) return 'Please enter your email address';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email_address)) {
        return 'Please enter a valid email address (e.g. name@example.com)';
      }
      return null;

    case 'address': {
      if (!formData.addresses?.[0]?.postcode?.trim()) return 'Please enter your postcode';
      if (!isValidUKPostcode(formData.addresses[0].postcode)) {
        return 'Postcode doesn\'t look right — expected format e.g. SW1A 1AA';
      }
      if (
        !formData.addresses?.[0]?.house_number?.trim() &&
        !formData.addresses?.[0]?.house_name?.trim()
      ) {
        return 'Please enter a house number or building name';
      }
      if (!formData.addresses?.[0]?.street?.trim()) return 'Please enter your street';
      if (!formData.addresses?.[0]?.town?.trim()) return 'Please enter your town/city';
      const allAddresses = formData.addresses || [];
      for (let i = 0; i < allAddresses.length; i++) {
        if (!allAddresses[i]?.residential_status) {
          return `Please select residential status for ${i === 0 ? 'your current address' : `previous address ${i}`}`;
        }
      }
      for (let i = 1; i < allAddresses.length; i++) {
        const a = allAddresses[i] || {};
        const incomplete =
          !a.postcode?.trim() ||
          (!a.house_number?.trim() && !a.house_name?.trim()) ||
          !a.street?.trim() ||
          !a.town?.trim() ||
          ((a.years || 0) === 0 && (a.months || 0) === 0);
        if (incomplete) {
          return `Previous Address ${i} is incomplete — please fill in the missing fields, or click the Remove button on that entry if it was added by mistake.`;
        }
        if (!isValidUKPostcode(a.postcode)) {
          return `Previous Address ${i}: postcode "${a.postcode}" doesn't look like a UK postcode. Expected format e.g. SW1A 1AA.`;
        }
      }
      const totalYears = allAddresses.reduce(
        (sum, addr) => sum + (addr.years || 0) + ((addr.months || 0) / 12),
        0
      );
      if (totalYears < 3) return 'You need at least 3 years of address history. Please add previous addresses.';
      return null;
    }

    case 'employment-status':
      if (!formData.employment_status) return 'Please select your employment status';
      return null;

    case 'employment-details': {
      const needsEmployer = EMPLOYED_STATUSES.includes(formData.employment_status);
      if (needsEmployer) {
        if (!formData.employer?.trim()) return 'Please enter employer name';
        if (!formData.job_title?.trim()) return 'Please enter job title';
        if (!formData.employer_address_same_as_home && !formData.employer_address?.postcode?.trim()) {
          return 'Please enter employer postcode';
        }
        if (
          !formData.employer_address_same_as_home &&
          formData.employer_address?.postcode &&
          !isValidUKPostcode(formData.employer_address.postcode)
        ) {
          return `Employer postcode "${formData.employer_address.postcode}" doesn't look like a UK postcode. Expected format e.g. SW1A 1AA.`;
        }
      }
      const history = formData.employment_history || [];
      for (let i = 1; i < history.length; i++) {
        const entry = history[i] || {};
        const status = entry.employment_status;
        if (!status) {
          return `Previous Employment ${i} is incomplete — please select an employment status or remove that entry using the Remove button.`;
        }
        const entryNeedsEmployer = EMPLOYED_STATUSES.includes(status);
        if (entryNeedsEmployer) {
          if (!entry.employer?.trim()) return `Previous Employment ${i}: please enter the employer name (or remove that entry).`;
          if (!entry.job_title?.trim()) return `Previous Employment ${i}: please enter the job title (or remove that entry).`;
        }
        if ((entry.years || 0) === 0 && (entry.months || 0) === 0) {
          return `Previous Employment ${i}: please enter how long they worked there (or remove that entry).`;
        }
      }
      return null;
    }

    case 'income':
      if (!formData.monthly_income?.trim() && !formData.gross_annual_income?.trim()) {
        return 'Please enter at least one income figure';
      }
      return additionalIncomeError(formData);

    case 'bank': {
      // Bank fields are optional overall — but if the applicant DOES provide
      // an account number, it must be a valid 8-digit UK format. OCR-extracted
      // values can be off (e.g. "1234567" with a missing leading zero, or
      // 9+ digits squashed from a neighbouring field).
      const acc = (formData.account_number || '').replace(/\D/g, '');
      if (formData.account_number && acc.length !== 8) {
        return `Account number must be exactly 8 digits (you have ${acc.length}). UK accounts are always 8 digits — please check the value.`;
      }
      return null;
    }

    case 'application-type': {
      // For Personal apps nothing to validate. For Business apps the broker
      // (Jigsaw Artis) hard-requires OrganisationDetail fields — missing City
      // returns `{"JigsawReferral.OrganisationDetail.City":"Required"}`. Catch
      // that here instead of letting the submission fail at the last step.
      if (formData.application_type !== 'Business') return null;
      const biz = formData.business_details || {};
      if (!biz.business_name?.trim()) return 'Please enter the business name';
      if (!biz.business_type) return 'Please select the business type';
      const ba = biz.business_address || {};
      if (!ba.postcode?.trim()) return 'Please enter the business postcode';
      if (!isValidUKPostcode(ba.postcode)) {
        return `Business postcode "${ba.postcode}" doesn't look like a UK postcode. Expected format e.g. SW1A 1AA.`;
      }
      if (!ba.building?.trim()) return 'Please enter the business building name or number';
      if (!ba.street?.trim()) return 'Please enter the business street';
      if (!ba.town?.trim()) return 'Please enter the business town/city (required by the broker)';
      return null;
    }
    case 'finance-type':
    case 'notes':
    case 'review':
      return null;

    default:
      return null;
  }
};

/**
 * Run validateStep across every step id (skipping 'review') and return
 * the list of failing steps. Used by ReviewStep to guarantee parity
 * with the per-step Next button gating.
 *
 * Returns: [{ stepId, stepIndex, label, error }, ...]
 */
export const validateAllSteps = (formData, applicationMode, opts = {}) => {
  const errors = [];
  for (const stepId of STEP_IDS) {
    if (stepId === 'review') continue;
    const err = validateStep(stepId, formData, applicationMode, opts);
    if (err) {
      errors.push({
        stepId,
        stepIndex: STEP_INDEX[stepId],
        label: STEP_LABELS[stepId] || stepId,
        error: err,
      });
    }
  }
  return errors;
};
